// src/components/layout/ClientPortalLayout.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar/Sidebar';
import ClientHeader from './ClientHeader';
import styles from './ClientPortalLayout.module.css';
import { useAuth } from '../../context/AuthContext';
import { useProfileGuard } from '../../hooks/useProfileGuard';

const STORAGE_KEY = 'client_sidebar_open';

const ClientPortalLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { loading: profileLoading } = useProfileGuard();

  const initialOpen = useMemo(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'true') return true;
    if (saved === 'false') return false;
    return typeof window !== 'undefined' ? window.innerWidth > 992 : true;
  }, []);

  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(initialOpen);

  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(isSidebarOpen));
  }, [isSidebarOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 992) setIsSidebarOpen(true);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!user || profileLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        Verificando perfil...
      </div>
    );
  }

  return (
    <div className={styles.layoutContainer}>
      <Sidebar
        userRole={user?.role || 'client'}
        isOpen={isSidebarOpen}
        onLinkClick={() => {
          if (window.innerWidth < 992) setIsSidebarOpen(false);
        }}
      />

      <div className={`${styles.mainWrapper} ${!isSidebarOpen ? styles.sidebarClosed : ''}`}>
        <ClientHeader onMenuToggle={toggleSidebar} />

        <main className={styles.contentContainer}>
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
};

export default ClientPortalLayout;
